import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { Stack, Row, Text, Badge, Panel, Input, Button, EmptyState, KycUploadCard } from '../ui';

const KIND_COLORS: Record<string, 'accent'|'positive'|'warning'|'neutral'> = {
  person: 'neutral', company: 'accent', fund: 'positive', gov: 'warning',
};

export function Crm() {
  const navigate = useNavigate();
  const [entities, setEntities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [hasPaid, setHasPaid] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ displayName: '', kind: 'company', country: '', primaryContactEmail: '' });
  const [creating, setCreating] = useState(false);
  const [prefillArtifactId, setPrefillArtifactId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.entities({ q: q || undefined, kind: kindFilter || undefined, has_payment: hasPaid || undefined })
      .then(r => setEntities(r.entities))
      .catch(() => setEntities([]))
      .finally(() => setLoading(false));
  }, [q, kindFilter, hasPaid]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newForm.displayName.trim()) return;
    setCreating(true);
    try {
      const res = await api.createEntity({
        kind: newForm.kind,
        displayName: newForm.displayName.trim(),
        country: newForm.country || undefined,
        primaryContactEmail: newForm.primaryContactEmail || undefined,
      });
      // Retroactively link prefill artifact
      if (prefillArtifactId && res.id) {
        api.linkArtifact(prefillArtifactId, { entityId: res.id }).catch(() => {});
        setPrefillArtifactId(null);
      }
      setShowNew(false);
      setNewForm({ displayName: '', kind: 'company', country: '', primaryContactEmail: '' });
      load();
      navigate(`/crm/${res.id}`);
    } catch (e: any) { alert(e.message); } finally { setCreating(false); }
  };

  return (
    <Stack gap={3}>
      <Row justify="between" align="center">
        <Text variant="display">CRM</Text>
        <Row gap={1}>
          <Button variant="secondary" size="sm" onClick={async () => { await api.backfillEntities(); load(); }}>backfill</Button>
          <Button variant="primary" size="sm" onClick={() => setShowNew(v => !v)}>+ new entity</Button>
        </Row>
      </Row>

      {showNew && (
        <Panel title="create entity">
          <Stack gap={2}>
            <KycUploadCard
              context="entity-prefill"
              onPrefill={(profile, artId) => {
                setPrefillArtifactId(artId);
                setNewForm((prev: any) => ({
                  ...prev,
                  displayName: profile.fullName || prev.displayName || '',
                  kind: profile.pep ? 'person' : prev.kind,
                  country: profile.country || prev.country || '',
                  primaryContactEmail: profile.primaryEmail || prev.primaryContactEmail || '',
                }));
              }}
            />
            <Row gap={2}>
              <Input placeholder="display name *" value={newForm.displayName} onChange={e => setNewForm(f => ({ ...f, displayName: e.target.value }))} className="flex-1" />
              <select value={newForm.kind} onChange={e => setNewForm(f => ({ ...f, kind: e.target.value }))} className="hairline rounded-sm px-2 h-9 text-sm bg-bg">
                <option value="company">company</option><option value="person">person</option><option value="fund">fund</option><option value="gov">gov</option>
              </select>
            </Row>
            <Row gap={2}>
              <Input placeholder="country" value={newForm.country} onChange={e => setNewForm(f => ({ ...f, country: e.target.value }))} className="flex-1" />
              <Input placeholder="email" value={newForm.primaryContactEmail} onChange={e => setNewForm(f => ({ ...f, primaryContactEmail: e.target.value }))} className="flex-1" />
            </Row>
            <Row gap={2} justify="end">
              <Button variant="secondary" size="sm" onClick={() => setShowNew(false)}>cancel</Button>
              <Button variant="primary" size="sm" onClick={handleCreate} disabled={creating}>{creating ? 'creating...' : 'create'}</Button>
            </Row>
          </Stack>
        </Panel>
      )}

      <Stack gap={2}>
        <Input placeholder="search entities…" value={q} onChange={e => setQ(e.target.value)} className="w-full" />
        <div className="flex flex-wrap gap-1">
          {['', 'company', 'person', 'fund', 'gov'].map(k => (
            <button key={k} onClick={() => setKindFilter(k)}
              className={`h-9 sm:h-7 px-3 sm:px-2 text-sm sm:text-xs rounded-sm hairline transition-colors ${kindFilter === k ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink'}`}>
              {k || 'all'}
            </button>
          ))}
          <button onClick={() => setHasPaid(v => !v)}
            className={`h-9 sm:h-7 px-3 sm:px-2 text-sm sm:text-xs rounded-sm hairline transition-colors ${hasPaid ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink'}`}>
            paid-me
          </button>
        </div>
      </Stack>

      {loading ? <Text muted variant="small">loading...</Text>
        : entities.length === 0 ? <EmptyState title="no entities yet" hint={hasPaid ? 'no entities where you received a payment' : 'click backfill to seed from existing data'} />
        : <Panel title={`${entities.length} ${entities.length === 1 ? 'entity' : 'entities'}`}>
            {/* Mobile cards */}
            <div className="md:hidden space-y-2">
              {entities.map((e: any) => (
                <button key={e.id} onClick={() => navigate(`/crm/${e.id}`)}
                  className="w-full text-left hairline rounded-sm p-3 hover:bg-whisper transition-colors">
                  <Row justify="between" align="center">
                    <Stack gap={0.5} className="min-w-0 flex-1">
                      <Text variant="small" className="font-medium truncate">{e.displayName}</Text>
                      <Row gap={1} wrap>
                        <Badge tone={KIND_COLORS[e.kind] ?? 'accent'}>{e.kind}</Badge>
                        {e.country && <Text variant="label">{e.country}</Text>}
                      </Row>
                    </Stack>
                    <Text variant="label" muted>{e.totalDeals ?? 0} deal{e.totalDeals !== 1 ? 's' : ''}</Text>
                  </Row>
                </button>
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="hairline-b">
                  <th className="text-left py-2 pr-3 text-muted font-normal">name</th>
                  <th className="text-left py-2 pr-3 text-muted font-normal">country</th>
                  <th className="text-left py-2 pr-3 text-muted font-normal">kind</th>
                  <th className="text-right py-2 pr-3 text-muted font-normal">deals</th>
                  <th className="text-left py-2 text-muted font-normal">last active</th>
                </tr></thead>
                <tbody>
                  {entities.map((e: any) => (
                    <tr key={e.id} className="hairline-b cursor-pointer hover:bg-whisper" onClick={() => navigate(`/crm/${e.id}`)}>
                      <td className="py-2 pr-3 font-medium">{e.displayName}</td>
                      <td className="py-2 pr-3 text-muted">{e.country ?? '–'}</td>
                      <td className="py-2 pr-3"><Badge tone={KIND_COLORS[e.kind] ?? 'accent'}>{e.kind}</Badge></td>
                      <td className="py-2 pr-3 text-right tnum">{e.totalDeals ?? 0}</td>
                      <td className="py-2 text-muted text-xs">{e.lastActiveAt ? new Date(e.lastActiveAt).toLocaleDateString() : '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>}
    </Stack>
  );
}
